/**
 * Agents, first: the team contract between the server, the web and (train 1c) the box.
 * Spec: docs/superpowers/specs/2026-09-19-agents-first-design.md (§3, §20 A1 to A3, A13).
 *
 * An actor is a row on the `agents` table with kind 'agent' or 'lead'. The soul is the row's
 * `content`, the handle is its `slug`, display fields live in `metadata`. Nothing here is a
 * second id system.
 */

export const AGENT_KINDS = ['template', 'agent', 'lead'] as const;
export type AgentKind = (typeof AGENT_KINDS)[number];

export const AGENT_STATUSES = ['active', 'trial', 'paused', 'retired'] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

/** Deterministic from the handle unless locked; an upload replaces it. */
export type AgentFace =
  | { kind: 'seed'; seed: string; locked: boolean }
  | { kind: 'upload'; url: string };

export interface TeamAgent {
  id: string;
  kind: 'agent' | 'lead';
  /** `agents.slug`. `@handle` everywhere a person addresses the agent. */
  handle: string;
  /** `metadata.name`. */
  name: string;
  /** `metadata.title`, one line under the name; '' when unset. */
  title: string;
  /** `metadata.description`, the one line. */
  description: string;
  /** `agents.content`; '' for the lead (Bash's voice is the composer's own). */
  soul: string;
  face: AgentFace;
  /** `kinIndexFor(id)`, 0..19; -1 for the lead, who wears copper. */
  kin: number;
  modelPin: string | null;
  status: AgentStatus;
  createdAt: string;
  /** Derived from the agent's thread and tasks (train 1b/1c); null until then. Never stored. */
  lastActiveAt: string | null;
  /** Derived from live tasks (train 1c); false until then. Never stored. */
  working: boolean;
}

export interface CreateAgentInput {
  name: string;
  description: string;
  soul: string;
  title?: string;
  /** Explicit handle; derived from the name when absent. */
  handle?: string;
  modelPin?: string | null;
  /** The template this agent was hired from (train 2); lands in `agents.source_id`. */
  sourceId?: string | null;
}

export interface UpdateAgentInput {
  name?: string;
  description?: string;
  soul?: string;
  title?: string;
  handle?: string;
  modelPin?: string | null;
  face?: AgentFace;
}

/** The lead's handle. Reserved: no hired agent can take it. */
export const LEAD_HANDLE = 'bash';

/** Handles no agent may take: the lead, and the words a room or a channel reads as people. */
export const RESERVED_HANDLES: readonly string[] = [
  LEAD_HANDLE, 'you', 'me', 'user', 'all', 'everyone', 'team', 'here', 'helper', 'system',
];

/** The twenty pastel tints of the kin face ladder (apps/web globals.css --kin-1 .. --kin-20). */
export const KIN_TINTS = 20;
